import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus,
  Search,
  MoreVertical,
  Pencil,
  Trash2,
  Facebook,
  Instagram,
  X,
  Tv,
  Radio,
  Signpost,
  Mail,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const platformOptions = [
  { id: "facebook", label: "Facebook", icon: Facebook, color: "text-blue-600" },
  { id: "instagram", label: "Instagram", icon: Instagram, color: "text-pink-600" },
  { id: "tiktok", label: "TikTok", icon: TikTokIcon, color: "text-black" },
  { id: "google", label: "Google Ads", icon: GoogleIcon, color: "text-blue-500" },
];

export default function ClientList({ clients, onClientSelect, onRefresh, API, clientType = "single" }) {
  const [search, setSearch] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    industry: "",
    contact_email: "",
    platforms: [],
    geo_targeting: "",
    audience_targeting: "",
    client_type: clientType,
    brand_channels: [],
  });

  const CHANNEL_PRESETS = [
    { name: "Local TV", icon: Tv },
    { name: "Local Radio", icon: Radio },
    { name: "Billboards / OOH", icon: Signpost },
    { name: "Print / Direct Mail", icon: Mail },
  ];

  const [newChannel, setNewChannel] = useState({ name: "", impressions: "", report_start: "", report_end: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const filteredClients = clients.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  const resetForm = () => {
    setFormData({
      name: "",
      industry: "",
      contact_email: "",
      platforms: [],
      geo_targeting: "",
      audience_targeting: "",
      client_type: clientType,
      brand_channels: [],
    });
    setNewChannel({ name: "", impressions: "", report_start: "", report_end: "" });
    setEditingClient(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const openEditDialog = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      industry: client.industry || "",
      contact_email: client.contact_email || "",
      platforms: client.platforms || [],
      geo_targeting: client.geo_targeting || "",
      audience_targeting: client.audience_targeting || "",
      client_type: client.client_type || clientType,
      brand_channels: client.brand_channels || [],
    });
    setNewChannel({ name: "", impressions: "", report_start: "", report_end: "" });
    setIsDialogOpen(true);
  };

  const handlePlatformToggle = (platformId) => {
    setFormData((prev) => ({
      ...prev,
      platforms: prev.platforms.includes(platformId)
        ? prev.platforms.filter((p) => p !== platformId)
        : [...prev.platforms, platformId],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("Client name is required");
      return;
    }

    setIsSubmitting(true);
    try {
      if (editingClient) {
        await axios.put(`${API}/clients/${editingClient.id}`, formData);
        toast.success("Client updated successfully");
      } else {
        await axios.post(`${API}/clients`, formData);
        toast.success("Client created successfully");
      }
      setIsDialogOpen(false);
      resetForm();
      onRefresh();
    } catch (e) {
      console.error("Error saving client:", e);
      toast.error("Failed to save client");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm("Are you sure you want to delete this client? All associated data will be removed.")) {
      return;
    }

    try {
      await axios.delete(`${API}/clients/${clientId}`);
      toast.success("Client deleted successfully");
      onRefresh();
    } catch (e) {
      console.error("Error deleting client:", e);
      toast.error("Failed to delete client");
    }
  };

  return (
    <div className="space-y-6" data-testid="client-list">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-['Outfit']">
            {clientType === "multi" ? "Clients, Multi Location" : "Clients, Single Location"}
          </h1>
          <p className="text-slate-500 mt-1">
            {clientType === "multi" ? "Clients with multiple locations or markets" : "Manage your single-location clients"}
          </p>
        </div>
        <Button
          onClick={openCreateDialog}
          className="bg-violet-600 hover:bg-violet-700 text-white rounded-full px-6 shadow-lg shadow-violet-500/20 btn-press"
          data-testid="add-client-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Client
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <Input
          type="text"
          placeholder="Search clients..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 bg-white border-slate-200 focus:border-violet-500 rounded-xl"
          data-testid="search-clients"
        />
      </div>

      {/* Clients Grid */}
      {filteredClients.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredClients.map((client, index) => (
            <motion.div
              key={client.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 card-hover"
              data-testid={`client-item-${client.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <button
                  onClick={() => onClientSelect(client)}
                  className="flex items-center gap-4 text-left hover:opacity-80 transition-opacity"
                  data-testid={`client-select-${client.id}`}
                >
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center text-white font-bold text-xl shadow-lg shadow-violet-500/20">
                    {client.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 text-lg">
                      {client.name}
                    </h3>
                    {client.industry && (
                      <p className="text-sm text-slate-500">{client.industry}</p>
                    )}
                  </div>
                </button>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
                      data-testid={`client-menu-${client.id}`}
                    >
                      <MoreVertical className="w-5 h-5 text-slate-400" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => openEditDialog(client)}
                      data-testid={`edit-client-${client.id}`}
                    >
                      <Pencil className="w-4 h-4 mr-2" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleDelete(client.id)}
                      className="text-red-600"
                      data-testid={`delete-client-${client.id}`}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {client.contact_email && (
                <p className="text-sm text-slate-500 mb-4 truncate">
                  {client.contact_email}
                </p>
              )}

              <div className="flex gap-3">
                {client.platforms?.map((platform) => {
                  const p = platformOptions.find((po) => po.id === platform);
                  if (!p) return null;
                  const Icon = p.icon;
                  return (
                    <div
                      key={platform}
                      className={`flex items-center gap-1 text-xs ${p.color} bg-slate-50 px-2 py-1 rounded-md`}
                    >
                      {typeof Icon === "function" ? <Icon /> : <Icon className="w-3 h-3" />}
                      <span className="capitalize">{platform}</span>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 bg-white rounded-xl border border-slate-100">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-slate-100 flex items-center justify-center">
            <Search className="w-8 h-8 text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-1">
            {search ? "No clients found" : "No clients yet"}
          </h3>
          <p className="text-slate-500 mb-4">
            {search
              ? "Try adjusting your search"
              : "Add your first client to get started"}
          </p>
          {!search && (
            <Button
              onClick={openCreateDialog}
              className="bg-violet-600 hover:bg-violet-700 text-white rounded-full"
              data-testid="add-first-client-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Client
            </Button>
          )}
        </div>
      )}

      {/* Client Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="client-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">
              {editingClient ? "Edit Client" : "Add New Client"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Client Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="e.g., Acme Corp"
                className="mt-1"
                data-testid="client-name-input"
              />
            </div>
            <div>
              <Label htmlFor="industry">Industry</Label>
              <Input
                id="industry"
                value={formData.industry}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, industry: e.target.value }))
                }
                placeholder="e.g., E-commerce"
                className="mt-1"
                data-testid="client-industry-input"
              />
            </div>
            <div>
              <Label htmlFor="email">Contact Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.contact_email}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    contact_email: e.target.value,
                  }))
                }
                placeholder="contact@example.com"
                className="mt-1"
                data-testid="client-email-input"
              />
            </div>
            <div>
              <Label>Platforms</Label>
              <div className="flex flex-wrap gap-x-4 gap-y-2 mt-2">
                {platformOptions.map((platform) => {
                  const Icon = platform.icon;
                  return (
                    <label
                      key={platform.id}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <Checkbox
                        checked={formData.platforms.includes(platform.id)}
                        onCheckedChange={() => handlePlatformToggle(platform.id)}
                        data-testid={`platform-${platform.id}`}
                      />
                      <span className={`flex items-center gap-1 ${platform.color}`}>
                        {typeof Icon === "function" ? <Icon /> : <Icon className="w-4 h-4" />}
                        {platform.label}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
            <div>
              <Label htmlFor="geo_targeting">Where Ads Run (Geographic)</Label>
              <Input
                id="geo_targeting"
                value={formData.geo_targeting}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, geo_targeting: e.target.value }))
                }
                placeholder="e.g., Chicago Metro Area, Northern Illinois"
                className="mt-1"
                data-testid="client-geo-input"
              />
            </div>
            <div>
              <Label htmlFor="audience_targeting">Target Audience</Label>
              <Input
                id="audience_targeting"
                value={formData.audience_targeting}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, audience_targeting: e.target.value }))
                }
                placeholder="e.g., Homeowners 35-65, interested in home improvement"
                className="mt-1"
                data-testid="client-audience-input"
              />
            </div>
            <div>
              <Label>Active Brand Channels</Label>
              <p className="text-xs text-slate-500 mt-0.5 mb-2">Add offline/broadcast channels like "Local TV CBS19" or "Local Radio WNRN"</p>
              
              {/* Existing channels */}
              {formData.brand_channels.length > 0 && (
                <div className="space-y-2 mb-3">
                  {formData.brand_channels.map((ch, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2 border border-slate-200">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{ch.name}</p>
                        <div className="flex gap-3 text-xs text-slate-500">
                          {ch.impressions && <span>{parseInt(ch.impressions).toLocaleString()} imp</span>}
                          {ch.report_start && <span>{ch.report_start} to {ch.report_end}</span>}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({
                          ...prev,
                          brand_channels: prev.brand_channels.filter((_, i) => i !== idx)
                        }))}
                        className="ml-2 p-1 text-slate-400 hover:text-red-500 transition-colors"
                        data-testid={`remove-channel-${idx}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Quick-add presets */}
              <div className="flex flex-wrap gap-1.5 mb-2">
                {CHANNEL_PRESETS.map((preset) => {
                  const PresetIcon = preset.icon;
                  return (
                    <button
                      key={preset.name}
                      type="button"
                      onClick={() => setNewChannel(prev => ({ ...prev, name: prev.name ? prev.name : preset.name }))}
                      className="flex items-center gap-1 text-xs bg-violet-50 text-violet-700 px-2 py-1 rounded-md hover:bg-violet-100 transition-colors border border-violet-200"
                    >
                      <PresetIcon className="w-3 h-3" />
                      {preset.name}
                    </button>
                  );
                })}
              </div>

              {/* Add new channel form */}
              <div className="flex gap-2">
                <Input
                  value={newChannel.name}
                  onChange={(e) => setNewChannel(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Local TV CBS19"
                  className="flex-1 text-sm"
                  data-testid="new-channel-name"
                />
                <Button
                  type="button"
                  size="sm"
                  disabled={!newChannel.name.trim()}
                  onClick={() => {
                    if (!newChannel.name.trim()) return;
                    setFormData(prev => ({
                      ...prev,
                      brand_channels: [...prev.brand_channels, { 
                        name: newChannel.name.trim(), 
                        status: "Active",
                        impressions: newChannel.impressions || "",
                        report_start: newChannel.report_start || "",
                        report_end: newChannel.report_end || "",
                      }]
                    }));
                    setNewChannel({ name: "", impressions: "", report_start: "", report_end: "" });
                  }}
                  className="bg-violet-600 hover:bg-violet-700 text-white rounded-md"
                  data-testid="add-channel-btn"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div>
              <Label>Client Type</Label>
              <div className="flex gap-3 mt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="client_type"
                    checked={formData.client_type === "single"}
                    onChange={() => setFormData((prev) => ({ ...prev, client_type: "single" }))}
                    className="accent-violet-600"
                    data-testid="client-type-single"
                  />
                  <span className="text-sm text-slate-700">Single Location</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="client_type"
                    checked={formData.client_type === "multi"}
                    onChange={() => setFormData((prev) => ({ ...prev, client_type: "multi" }))}
                    className="accent-violet-600"
                    data-testid="client-type-multi"
                  />
                  <span className="text-sm text-slate-700">Multi Location</span>
                </label>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                className="rounded-full"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-violet-600 hover:bg-violet-700 text-white rounded-full"
                data-testid="save-client-btn"
              >
                {isSubmitting ? "Saving..." : editingClient ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
